import type { PricesEntry } from '@cocoonspace/sdk-js/types/pricesEntry'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useGetSpacePrices } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'

import { BOOKING_MAX_DATE } from './booking-stepper'
import { isMultidateSelectableDay } from './get-available-hours-for-day'

const isSelectableDay = (
	entry: PricesEntry,
	{
		minAvailableHours,
		spaceHours,
	}: {
		minAvailableHours?: number
		spaceHours?: Space['hours']
	},
) => {
	if (minAvailableHours) {
		return isMultidateSelectableDay(entry, minAvailableHours, spaceHours)
	}

	const { closed, available } = entry

	return !closed && !!available
}

const isDisabledDay = (
	entry: PricesEntry,
	filterOptions: {
		minAvailableHours?: number
		spaceHours?: Space['hours']
	},
) => {
	if (filterOptions.minAvailableHours) {
		return !isSelectableDay(entry, filterOptions)
	}

	const { closed, available } = entry

	return !!closed || available === false
}

export const usePricesByDates = ({
	space,
	onlyEmptyDays = false,
	minAvailableHours,
}: {
	space: Space
	onlyEmptyDays?: boolean
	minAvailableHours?: number
}) => {
	return useGetSpacePrices(
		space.id,
		{ only_empty_days: onlyEmptyDays, to: BOOKING_MAX_DATE },
		{
			query: {
				select: ({ data }) => {
					const filterOptions = {
						minAvailableHours,
						spaceHours: space.hours,
					}

					const pricesByDay = data
						?.filter((entry) => isSelectableDay(entry, filterOptions))
						.reduce<Record<string, PricesEntry>>((acc, item) => {
							acc[item.day] = item
							return acc
						}, {})

					const disabled = data
						?.filter((entry) => isDisabledDay(entry, filterOptions))
						.map(({ day }) => new Date(day))

					return {
						disabled,
						pricesByDay,
					}
				},
			},
		},
	)
}
